<template>
  <div class="wf-chat-panel">
    <div class="wf-chat-panel-content" :ref="messagesRef">
      <div v-if="isLoading === true && messages.length === 0" class="wf-chat-panel-loading">
        <span class="loading loading-dots loading-md"></span>
      </div>
      <div v-if="messages.length === 0 && isLoading === false" class="wf-chat-empty">
        <div class="wf-chat-empty-title">Tell me what you want to happen</div>
        <div class="wf-chat-empty-subtitle">I can add nodes, connect them, and fill fields for you</div>
      </div>
      <div
        v-for="message in messages"
        :key="`workflow-chat-message-${message.id}`"
        :class="message.type === 'user' ? 'wf-chat-bubble wf-chat-bubble-user' : 'wf-chat-bubble wf-chat-bubble-assistant'"
        :style="{ direction: message.direction, textAlign: message.direction === 'rtl' ? 'right' : 'left' }"
      >
        <template v-if="message.isLoading === true">
          <svg width="40" height="10" viewBox="0 0 120 30" xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="wf-chat-typing-dots"><circle cx="15" cy="15" r="15"><animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15" calcMode="linear" repeatCount="indefinite" /><animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1" calcMode="linear" repeatCount="indefinite" /></circle><circle cx="60" cy="15" r="9" fill-opacity="0.3"><animate attributeName="r" from="9" to="9" begin="0s" dur="0.8s" values="9;15;9" calcMode="linear" repeatCount="indefinite" /><animate attributeName="fill-opacity" from="0.5" to="0.5" begin="0s" dur="0.8s" values=".5;1;.5" calcMode="linear" repeatCount="indefinite" /></circle><circle cx="105" cy="15" r="15"><animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15" calcMode="linear" repeatCount="indefinite" /><animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1" calcMode="linear" repeatCount="indefinite" /></circle></svg>
        </template>
        <template v-else>
          <div v-if="message.type === 'assistant'" v-html="sanitizeHtml(message.text)" class="wf-chat-bubble-content"></div>
          <div v-else class="wf-chat-bubble-content">{{ message.rawText }}</div>
        </template>
      </div>
    </div>
    <div class="wf-chat-panel-input-area">
      <div v-if="quickPrompts.length > 0" class="wf-chat-quick-prompts">
        <button
          v-for="item in quickPrompts"
          :key="`wf-quick-${item}`"
          class="wf-chat-quick-prompt"
          @click="emit('quick', item)"
        >
          {{ item }}
        </button>
      </div>
      <div class="wf-chat-panel-input-row">
        <textarea
          :value="modelValue"
          class="wf-chat-panel-input"
          placeholder="Describe what should happen in plain language..."
          @input="emit('update:modelValue', $event.target.value)"
          @keyup.enter.prevent="emit('send')"
        ></textarea>
        <button class="wf-chat-panel-send" @click="emit('send')">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="none" stroke="#ffffff" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m5 12l-.604-5.437C4.223 5.007 5.825 3.864 7.24 4.535l11.944 5.658c1.525.722 1.525 2.892 0 3.614L7.24 19.466c-1.415.67-3.017-.472-2.844-2.028zm0 0h7"/></svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { sanitizeHtml } from '@/shared/utils/sanitizeHtml';

defineProps({
  messages: {
    type: Array,
    default: () => []
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  modelValue: {
    type: String,
    default: ''
  },
  messagesRef: {
    type: Object,
    default: null
  },
  quickPrompts: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:modelValue', 'send', 'quick']);
</script>

<style scoped>
.wf-chat-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.wf-chat-panel-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.wf-chat-panel-content > * + * {
  margin-top: 12px;
}

.wf-chat-panel-loading {
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wf-chat-empty {
  margin-top: auto;
  margin-bottom: auto;
  text-align: center;
  padding: 20px;
}

.wf-chat-empty-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.wf-chat-empty-subtitle {
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.5;
}

.wf-chat-bubble {
  max-width: 90%;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 0.85rem;
  line-height: 1.5;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.wf-chat-bubble-user {
  align-self: flex-end;
  background: #4f46e5;
  color: #ffffff;
  border-bottom-right-radius: 4px;
}

.wf-chat-bubble-assistant {
  align-self: flex-start;
  background: #f9fafb;
  color: #111827;
  border: 1px solid #e5e7eb;
  border-bottom-left-radius: 4px;
}

.wf-chat-bubble-content :deep(p) {
  margin: 0;
}

.wf-chat-typing-dots {
  color: #9ca3af;
}

.wf-chat-panel-input-area {
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  padding: 12px 16px;
}

.wf-chat-quick-prompts {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 10px;
  row-gap: 8px;
}

.wf-chat-quick-prompts > * + * {
  margin-left: 8px;
}

.wf-chat-quick-prompt {
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #374151;
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 0.7rem;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.wf-chat-quick-prompt:hover {
  border-color: #6366f1;
  color: #4338ca;
  background: #eef2ff;
}

.wf-chat-panel-input-row {
  display: flex;
  align-items: flex-end;
}

.wf-chat-panel-input-row > * + * {
  margin-left: 10px;
}

.wf-chat-panel-input {
  flex: 1;
  min-width: 0;
  min-height: 44px;
  max-height: 140px;
  resize: none;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.85rem;
  line-height: 1.4;
  color: #111827;
  padding: 10px 12px;
  background: #ffffff;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.wf-chat-panel-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
}

.wf-chat-panel-send {
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 8px;
  background: #4f46e5;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px 0 rgba(79, 70, 229, 0.3);
}

.wf-chat-panel-send:hover {
  background: #4338ca;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(79, 70, 229, 0.2), 0 2px 4px -1px rgba(79, 70, 229, 0.1);
}

.wf-chat-panel-send:active {
  transform: translateY(0);
}
</style>
